import { useQueries, useQuery } from "@tanstack/react-query";
import { ApiError, apiGet } from "./api";
import { useFeedStatus } from "./stream";
import { historyWindow, type HistoryRange } from "./history";
import { espnSport, espnTeamId, mergeNews, parseEspnNews } from "./news";
import type { Contest, League, MarketDef, Partner, Position, PriceHistory } from "./types";

const MIN = 60_000;
/** Look this far ahead for games… */
const UPCOMING_DAYS = 7;
/** …and this far back, so games already under way stay on the board. */
const STARTED_WITHIN_H = 6;
const MAX_PAGES = 4;
const WINDOW_ROUND_MS = 10 * MIN;

export function useLeagues() {
  return useQuery({
    queryKey: ["leagues"],
    queryFn: async ({ signal }) => (await apiGet<League[]>("/leagues", { limit: 200 }, signal)).data,
    staleTime: 60 * MIN,
  });
}

export function usePartners() {
  return useQuery({
    queryKey: ["partners"],
    queryFn: async ({ signal }) => {
      const list = (await apiGet<Partner[]>("/partners", {}, signal)).data;
      return new Map(list.map((p) => [p.partner_id, p]));
    },
    staleTime: 60 * MIN,
  });
}

function startsAt(c: Contest): number {
  const t = c.starts_at ? Date.parse(c.starts_at) : NaN;
  return Number.isNaN(t) ? Number.MAX_SAFE_INTEGER : t;
}

/** An odds screen compares games: futures ("tournament") and market-less contests are out. */
function games(list: Contest[]): Contest[] {
  return list
    .filter((c) => c.type !== "tournament" && c.has_markets !== false)
    .sort((a, b) => startsAt(a) - startsAt(b));
}

/**
 * Every open game across all leagues that starts within the week (or started in the last few
 * hours), soonest first.
 */
export function useUpcomingContests() {
  return useQuery({
    queryKey: ["contests", "upcoming"],
    queryFn: async ({ signal }) => {
      // Rounded to 10 minutes so every signed-out visitor asks the public feed the same question
      // and shares one edge-cache entry.
      const now = Math.floor(Date.now() / WINDOW_ROUND_MS) * WINDOW_ROUND_MS;
      const base = {
        status: "open",
        start_after: new Date(now - STARTED_WITHIN_H * 3_600_000).toISOString(),
        start_before: new Date(now + UPCOMING_DAYS * 86_400_000).toISOString(),
        limit: 200,
      };
      const all: Contest[] = [];
      let cursor: string | undefined;
      for (let page = 0; page < MAX_PAGES; page++) {
        const res = await apiGet<Contest[]>("/contests", { ...base, cursor }, signal);
        all.push(...res.data);
        const next = res.pagination?.has_more ? res.pagination.next_cursor : null;
        if (!next) break;
        cursor = next;
      }
      return games(all);
    },
    staleTime: 5 * MIN,
    refetchInterval: 10 * MIN,
  });
}

/**
 * Open games whose title matches `q` — the API's own case-insensitive title search, across every
 * league and date, not just what the board has loaded. `null` disables it.
 */
export function useContestSearch(q: string | null) {
  return useQuery({
    queryKey: ["contests", "search", q],
    enabled: !!q,
    queryFn: async ({ signal }) =>
      games((await apiGet<Contest[]>("/contests", { q: q ?? "", status: "open", limit: 50 }, signal)).data),
    staleTime: MIN,
  });
}

/** A game's market definitions: labels, shape flags and the category that separates player props. */
export function useContestMarkets(contestId: string | null) {
  return useQuery({
    queryKey: ["markets", contestId],
    enabled: !!contestId,
    queryFn: async ({ signal }) =>
      (await apiGet<MarketDef[]>(`/contests/${encodeURIComponent(contestId ?? "")}/markets`, {}, signal)).data,
    staleTime: 60 * MIN,
  });
}

/** Each game's liquidity. The stream keeps these current; polling is the fallback without it. */
export function useContestLiquidity(ids: string[]) {
  const feed = useFeedStatus();
  return useQueries({
    queries: ids.map((id) => ({
      queryKey: ["liquidity", id],
      queryFn: async ({ signal }: { signal: AbortSignal }) =>
        (await apiGet<Position[]>(`/contests/${encodeURIComponent(id)}/liquidity`, {}, signal)).data,
      staleTime: 30_000,
      refetchInterval: feed === "live" ? 5 * MIN : MIN,
    })),
  });
}

/** One game by id, for a game link opened cold (the game isn't on the loaded board). */
export function useContest(id: string | null, known?: Contest) {
  return useQuery({
    queryKey: ["contest", id],
    enabled: !!id && !known,
    queryFn: async ({ signal }) =>
      (await apiGet<Contest>(`/contests/${encodeURIComponent(id ?? "")}`, {}, signal)).data,
    initialData: known,
    staleTime: 5 * MIN,
  });
}

/**
 * Price history isn't available to this viewer: the key has no `price_history` entitlement (403),
 * or the public feed doesn't serve it (404 `not_found` — the API itself answers an unknown game
 * with an empty 200, so a 404 can only be the feed's allowlist).
 */
export function isHistoryLocked(err: unknown): boolean {
  return err instanceof ApiError && (err.status === 403 || (err.status === 404 && err.code === "not_found"));
}

/**
 * Price history for the given positions (one book's sides). A 403 is an answer, not a failure:
 * the key isn't entitled, so it isn't retried.
 */
export function usePriceHistory(contestId: string | null, hashes: string[], range: HistoryRange) {
  const key = [...hashes].sort().join(",");
  return useQuery({
    queryKey: ["price-history", contestId, key, range],
    enabled: !!contestId && hashes.length > 0,
    queryFn: async ({ signal }) => {
      const { from, to } = historyWindow(range);
      return (
        await apiGet<PriceHistory>(
          `/contests/${encodeURIComponent(contestId ?? "")}/price-history`,
          { from, to, resolution: "auto", position_hash: key, limit: 2000 },
          signal,
        )
      ).data;
    },
    retry: (count, err) => !isHistoryLocked(err) && count < 1,
    staleTime: MIN,
    // Once the answer is "not entitled", stop asking: that won't change while the page is open.
    refetchInterval: (query) => (isHistoryLocked(query.state.error) ? false : 2 * MIN),
  });
}

/**
 * MOCKUP (dev only): the two teams' headlines from ESPN's public feed, for the chart's news pins.
 * Production would read news through our own server, not from the browser.
 */
export function useTeamNews(league: string | undefined, teams: string[], enabled: boolean) {
  const sport = espnSport(league);
  return useQuery({
    queryKey: ["team-news", sport, ...teams],
    enabled: enabled && !!sport && teams.length > 0,
    queryFn: async ({ signal }) => {
      // Through the dev server's /dev/espn proxy (vite.config.ts): the mockup is dev only.
      const base = `/dev/espn/apis/site/v2/sports/${sport}`;
      const get = async (url: string) => {
        const res = await fetch(url, { signal });
        if (!res.ok) throw new Error(`ESPN ${res.status}`);
        return res.json() as Promise<unknown>;
      };
      const all = await get(`${base}/teams?limit=500`);
      const ids = teams.map((t) => espnTeamId(all, t)).filter((id): id is string => !!id);
      const lists = await Promise.all(ids.map(async (id) => parseEspnNews(await get(`${base}/news?team=${id}&limit=50`))));
      return mergeNews(lists);
    },
    staleTime: 10 * MIN,
  });
}
